import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, BarChart3, Users, TrendingUp, Layers, Shield, Brain, Network, Clock, AlertCircle } from 'lucide-react';
import { ContactSection } from '../../components/ContactSection';

/* ─── Icon ─── */
const Icon = ({ d, size = 20, className = '', strokeWidth = 1.5, style = {} }: {
  d: string; size?: number; className?: string; strokeWidth?: number; style?: React.CSSProperties;
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    className={className} style={style}>
    <path d={d} />
  </svg>
);

const iconPaths: Record<string, string> = {
  check: 'M20 6L9 17l-5-5',
  x: 'M18 6L6 18M6 6l12 12',
  zap: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
  arrow: 'M5 12h14m-7-7 7 7-7 7',
  msg: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  bar: 'M12 20V10m6 10V4M6 20v-4',
};

/* ─── ParticleCanvas ─── */
type Particle = { x: number; y: number; vx: number; vy: number; r: number; pulse: number };

const ParticleCanvas = ({ color = '#ff5d22', count = 70, maxDist = 130 }: {
  color?: string; count?: number; maxDist?: number;
}) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    let w: number, h: number, pts: Particle[], raf: number;
    const init = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      pts = Array.from({ length: count }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.5, vy: (Math.random() - 0.5) * 0.5,
        r: Math.random() * 1.8 + 0.5, pulse: Math.random() * Math.PI * 2,
      }));
    };
    const hex2rgb = (h: string) => [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];
    const [r, g, b] = hex2rgb(color);
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.pulse += 0.02;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < maxDist) {
            const alpha = (1 - d / maxDist) * 0.25;
            ctx.beginPath(); ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
            ctx.lineWidth = 0.7; ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.stroke();
          }
        }
      }
      pts.forEach(p => {
        const pr = p.r * (1 + 0.3 * Math.sin(p.pulse));
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, pr * 3);
        grad.addColorStop(0, `rgba(${r},${g},${b},0.9)`); grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.beginPath(); ctx.arc(p.x, p.y, pr * 3, 0, Math.PI * 2); ctx.fillStyle = grad; ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    const ro = new ResizeObserver(init); ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [color, count, maxDist]);
  return <canvas ref={ref} style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }} />;
};

/* ─── GridFloor ─── */
const GridFloor = ({ opacity = 0.07 }: { opacity?: number }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    let w: number, h: number, raf: number, t = 0;
    const init = () => { w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight; };
    const draw = () => {
      t += 0.003; ctx.clearRect(0, 0, w, h);
      const cols = 20, rows = 14, cw = w / cols, ch = h / rows;
      for (let i = 0; i <= cols; i++) {
        const x = i * cw; const wave = Math.sin(i * 0.4 + t) * 8;
        ctx.beginPath(); ctx.strokeStyle = `rgba(227,30,36,${opacity * (0.5 + 0.5 * Math.sin(i * 0.3 + t))})`;
        ctx.lineWidth = 0.5; ctx.moveTo(x, 0); ctx.lineTo(x, h + wave); ctx.stroke();
      }
      for (let j = 0; j <= rows; j++) {
        const y = j * ch; const wave = Math.sin(j * 0.5 + t) * 5;
        ctx.beginPath(); ctx.strokeStyle = `rgba(227,30,36,${opacity * (0.5 + 0.5 * Math.cos(j * 0.3 + t))})`;
        ctx.lineWidth = 0.5; ctx.moveTo(0, y + wave); ctx.lineTo(w, y + wave); ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    const ro = new ResizeObserver(init); ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [opacity]);
  return <canvas ref={ref} style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }} />;
};

/* ─── Orb ─── */
const Orb = ({ style, color, size, animDur, animDelay = 0 }: {
  style?: React.CSSProperties; color: string; size: number; animDur: number; animDelay?: number;
}) => (
  <div style={{
    position:'absolute', borderRadius:'50%', filter:`blur(${size * 0.35}px)`,
    width:size, height:size, background:color, opacity:0.15,
    animation:`orbFloat ${animDur}s ease-in-out ${animDelay}s infinite`, ...style,
  }} />
);

/* ─── Reveal ─── */
const Reveal = ({ children, delay = 0, style = {}, className = '' }: {
  children: React.ReactNode; delay?: number; style?: React.CSSProperties; className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? 'translateY(0) scale(1)' : 'translateY(32px) scale(0.97)',
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`, ...style,
    }}>
      {children}
    </div>
  );
};

/* ─── TiltCard ─── */
const TiltCard = ({ children, className = '', style = {} }: {
  children: React.ReactNode; className?: string; style?: React.CSSProperties;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 14;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -14;
    ref.current.style.transform = `perspective(800px) rotateY(${x}deg) rotateX(${y}deg) translateY(-6px)`;
  };
  const handleLeave = () => {
    if (ref.current) ref.current.style.transform = 'perspective(800px) rotateY(0) rotateX(0) translateY(0)';
  };
  return (
    <div ref={ref} className={className}
      style={{ transition:'transform 0.3s ease', transformStyle:'preserve-3d', ...style }}
      onMouseMove={handleMove} onMouseLeave={handleLeave}>
      {children}
    </div>
  );
};

/* ─── FAQItem ─── */
const FAQItem = ({ q, a, idx }: { q: string; a: string; idx: number }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={idx * 0.08}>
      <div onClick={() => setOpen(!open)} style={{
        background: open ? 'linear-gradient(135deg,#0f0f0f,#1a1a1a)' : '#fff',
        border: `1px solid ${open ? 'rgba(227,30,36,0.5)' : '#e8e8e8'}`,
        borderRadius:12, padding:'16px 20px', cursor:'pointer',
        transition:'all 0.35s ease',
        boxShadow: open ? '0 8px 40px rgba(227,30,36,0.15)' : '0 2px 12px rgba(0,0,0,0.05)',
        marginBottom:10,
      }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 }}>
          <span style={{ fontWeight:700, fontSize:14, color: open ? '#fff' : '#0f0f0f', letterSpacing:'-0.01em' }}>{q}</span>
          <div style={{
            width:28, height:28, borderRadius:'50%', background: open ? '#ff5d22' : '#f4f4f4',
            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
            transition:'all 0.3s ease', transform: open ? 'rotate(45deg)' : 'rotate(0)',
          }}>
            <Icon d="M12 5v14M5 12h14" size={12} style={{ color: open ? '#fff' : '#666' }} />
          </div>
        </div>
        <div style={{ overflow:'hidden', maxHeight: open ? 200 : 0, transition:'max-height 0.4s ease', marginTop: open ? 12 : 0 }}>
          <p style={{ fontSize:13, lineHeight:1.6, color:'rgba(255,255,255,0.72)', margin:0 }}>{a}</p>
        </div>
      </div>
    </Reveal>
  );
};

/* ─────────────────── DATA ─────────────────── */
const T = {
  stats: [
    { val: '6x',    unit: 'Faster', label: 'Launch of digital monetization offers across markets' },
    { val: '40%',   unit: '',       label: 'Improvement in billing accuracy through automated rating and reconciliation' },
    { val: '200%',  unit: 'ROI',    label: 'Potential upside from cloud, communications, AI, and partner services' },
  ],
  challenges: [
    { Icon: TrendingUp,  title: 'Connectivity margin pressure', desc: 'Core connectivity growth is under pressure while enterprise buyers expect richer digital services.' },
    { Icon: Network,     title: 'Fragmented partner ecosystem', desc: 'Cloud, UCaaS, CCaaS, CPaaS, AI, and ISV partners are difficult to onboard and monetize through legacy systems.' },
    { Icon: Clock,       title: 'Slow time-to-market',         desc: 'New product launches take quarters, not weeks — ceding ground to cloud-native competitors.' },
    { Icon: AlertCircle, title: 'Disconnected monetization stack', desc: 'Marketplace, CPQ, billing, provisioning, partner operations, tax, and support data sit in separate workflows.' },
  ],
  comparison: [
    { f: 'Implementation cycles',   ours: 'Live in weeks with high-velocity framework',      theirs: 'Months-long implementation cycles' },
    { f: 'Billing reconciliation',  ours: 'Automated billing with 40% accuracy lift',        theirs: 'Manual billing reconciliation and errors' },
    { f: 'Hyperscaler integration', ours: 'Deep integrations with AWS, Azure, Google Cloud', theirs: 'No native hyperscaler integration' },
    { f: 'Product catalog',         ours: 'Flexible, no-code product bundling',              theirs: 'Rigid, configuration-heavy product catalog' },
    { f: 'Partner self-serve',      ours: 'Low-touch, self-serve partner marketplace',       theirs: 'Partner self-serve is an afterthought' },
  ],
  whyFeatures: [
    'Launch cloud marketplace, UCaaS, CCaaS, CPaaS, AI marketplace, and partner ecosystem offers from one operating layer',
    'Onboard hyperscalers, ISVs, communications partners, AI vendors, and channel partners with governed workflows',
    'Support subscription, usage, seat, bundled, wholesale, and reseller billing through one automated monetization engine',
    'Unify quote, order, provisioning, invoice, support, and renewal workflows so digital services scale without manual effort',
    'Real-time dashboards across products, partners, and geographies — decisions from data, not gut',
  ],
  pillars: [
    { LucideIcon: Globe,     title: 'Digital services marketplace', desc: 'Branded storefronts for cloud, UCaaS, CCaaS, CPaaS, AI, SaaS, and connectivity-led bundles.' },
    { LucideIcon: BarChart3, title: 'Telecom-grade monetization',   desc: 'Subscription, usage, hybrid, reseller, and wholesale billing with tax, rating, and reconciliation controls.' },
    { LucideIcon: Users,     title: 'Partner ecosystem operations', desc: 'Partner onboarding, tiering, deal flows, incentive management, support routing, and performance reporting.' },
  ],
  features: [
    { LucideIcon: TrendingUp, title: 'Revenue analytics',       desc: 'Real-time dashboards covering MRR, churn, partner contribution, and product-level margin.' },
    { LucideIcon: Layers,     title: 'ERP & CRM integration',   desc: 'Pre-built connectors to Salesforce, SAP, Oracle, and major hyperscaler marketplaces.' },
    { LucideIcon: Shield,     title: 'Compliance & security',   desc: 'SOC 2 Type II and ISO 27001 certified, with 99.9% SLA and enterprise-grade data residency.' },
    { LucideIcon: Globe,      title: 'Digital marketplace',     desc: 'Cloud marketplace, UCaaS, CCaaS, CPaaS, AI marketplace, and partner offer packaging.' },
    { LucideIcon: BarChart3,  title: 'Automated billing engine',desc: 'Usage-based, subscription, and hybrid billing with rating, mediation, tax, and multi-currency support.' },
    { LucideIcon: Users,      title: 'Partner lifecycle mgmt',  desc: 'End-to-end partner onboarding, tiering, incentive management, and performance reporting.' },
  ],
  aiCapabilities: [
    'Intelligent product recommendations', 'Predictive revenue forecasting',
    'Churn risk scoring', 'Dynamic pricing optimization',
    'Automated billing anomaly detection', 'Partner performance insights',
    'Propensity-to-buy modeling', 'Natural language catalog search',
  ],
  platformFeatures: [
    { val: '6 wks', label: 'Pilot to production',    desc: 'High-velocity deployment gets your marketplace live in weeks, not quarters.' },
    { val: '99.9%', label: 'Platform availability',  desc: 'SOC 2 and ISO-certified infrastructure with enterprise SLA and 24/7 support.' },
    { val: '50+',   label: 'Pre-built integrations', desc: 'Native connectors to AWS, Azure, GCP, Salesforce, SAP, and leading ISV catalogs.' },
    { val: '12+',   label: 'Markets supported',      desc: 'Multi-currency, multi-tax, and multi-language across all major global regions.' },
  ],
  results: [
    { val: '6x',     label: 'Faster marketplace launch vs. legacy BSS rollout',     org: 'Regional telco operator, 12 markets' },
    { val: '40%',    label: 'Reduction in billing disputes after automation rollout', org: 'Global IT distributor' },
    { val: '30–50%', label: 'Partner efficiency gain with unified operations stack',  org: 'Tier-1 telecom, APAC region' },
  ],
  faq: [
    { q: 'How quickly can a telco go live with AppGallop?',
      a: 'Most operators are fully operational within 6 weeks. Our high-velocity framework handles catalog setup, vendor integrations, and partner portal configuration end to end.' },
    { q: 'Does AppGallop support multi-vendor and multi-cloud environments?',
      a: 'Yes. The platform is purpose-built for multi-cloud telco environments with deep native integrations to AWS, Azure, Google Cloud, and all major ISV partners out of the box.' },
    { q: 'Can we white-label the marketplace for our partners and resellers?',
      a: 'Fully. Your partners see your brand, your domain, and your pricing. The AppGallop platform remains invisible to them.' },
    { q: 'How does the AI layer help with revenue outcomes?',
      a: 'AppGallop AI surfaces pricing signals, predicts churn before it happens, recommends bundle configurations, and identifies upsell opportunities — all in real time from your own usage data.' },
  ],
};

/* ─────────────────── PAGE ─────────────────── */
export default function TelcoToTechcoPage() {
  return (
    <div className="bg-white overflow-x-hidden">
      <style>{`
        @keyframes orbFloat { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(40px,-30px) scale(1.08)} 66%{transform:translate(-25px,20px) scale(0.95)} }
        @keyframes pulse3d  { 0%,100%{box-shadow:0 0 0 0 rgba(227,30,36,0.4)} 50%{box-shadow:0 0 0 20px rgba(227,30,36,0)} }
        .card-3d { transition:transform 0.3s ease,box-shadow 0.3s ease }
        .card-3d:hover { transform:translateY(-8px);box-shadow:0 32px 64px rgba(0,0,0,0.18) }
      `}</style>

      {/* ─── HERO ─── */}
      <section className="relative min-h-screen flex flex-col justify-center bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden px-6 pb-12" style={{ paddingTop:'80px' }}>
        <ParticleCanvas count={80} color="#ff5d22" />
        <GridFloor opacity={0.04} />
        <Orb style={{ top:'-5%', right:'-8%' }} color="#ff5d22" size={500} animDur={18} />
        <Orb style={{ bottom:'-10%', left:'-5%' }} color="#3b82f6" size={400} animDur={22} animDelay={3} />

        <div className="relative z-20 max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/6 text-slate-700 text-[9px] font-black uppercase tracking-widest mb-5 border border-slate-200">
                <svg width={8} height={8} viewBox="0 0 10 10"><circle cx={5} cy={5} r={5} fill="#ff5d22" /></svg>
                Telco Digital Monetization
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.08] text-slate-950 mb-5">
                Accelerate Your Journey from{' '}
                <span className="bg-gradient-to-r from-[#ff5d22] to-red-500 bg-clip-text text-transparent">
                  Telco to TechCo
                </span>
              </h1>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="text-base text-slate-600 leading-relaxed mb-7 max-w-lg font-medium">
                Telecom operators are sitting on distribution reach, trusted relationships, and billing infrastructure that digital businesses would pay anything for. AppGallop turns that latent advantage into recurring revenue without years of transformation.
              </p>
            </Reveal>
            <Reveal delay={0.20}>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact">
                  <button className="px-7 py-3 rounded-lg bg-[#ff5d22] text-white font-black text-[10px] uppercase tracking-widest border-none cursor-pointer hover:bg-[#d41820] transition-all duration-300" style={{ animation:'pulse3d 3s ease infinite' }}>
                    Book a Demo
                  </button>
                </Link>
                <Link to="/solutions">
                  <button className="px-7 py-3 rounded-lg bg-white text-slate-700 font-black text-[10px] uppercase tracking-widest border border-slate-300 cursor-pointer hover:bg-slate-50 transition-all duration-300">
                    Explore Solutions
                  </button>
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Stats cluster */}
          <div className="grid gap-3">
            {T.stats.map((s, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <TiltCard className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" style={{ position:'relative' }}>
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/40 to-transparent" />
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="text-4xl font-black text-[#2563EB] leading-none">{s.val}</div>
                      <div className="text-[9px] text-slate-400 font-black uppercase tracking-wider mt-0.5">{s.unit || 'metric'}</div>
                    </div>
                    <div className="w-px h-10 bg-slate-200" />
                    <div className="text-xs text-slate-600 font-medium leading-relaxed">{s.label}</div>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CHALLENGE (dark) ─── */}
      <section className="relative bg-slate-950 py-20 px-6 overflow-hidden">
        <ParticleCanvas count={50} color="#ff5d22" />
        <Orb style={{ top:'15%', right:'-8%' }} color="#ff5d22" size={350} animDur={20} />
        <div className="relative z-20 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
                The Challenge
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white mb-3">
                Telcos face a structural growth trap{' '}                
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="text-sm text-white/50 max-w-xl mx-auto leading-relaxed font-medium">
                Legacy connectivity revenues are declining. Cloud, SaaS, and digital services are where the margin is but getting there requires platform capabilities most telcos do not yet have.
              </p>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {T.challenges.map((item, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <TiltCard className="bg-white/3 border border-white/8 rounded-lg p-6 backdrop-blur-xl relative overflow-hidden h-full">
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ff5d22]/50 to-transparent" />
                  <div className="w-10 h-10 rounded-lg bg-[#ff5d22]/15 border border-[#ff5d22]/30 flex items-center justify-center mb-3">
                    <item.Icon size={18} className="text-[#ff5d22]" />
                  </div>
                  <h3 className="text-base font-black text-white mb-2 tracking-tight">{item.title}</h3>
                  <p className="text-xs text-white/55 leading-relaxed font-medium">{item.desc}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COMPARISON (light) ─── */}
      <section className="relative bg-slate-50 py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(227,30,36,0.03)_1px,transparent_1px)] bg-[length:30px_30px]" />
        <Orb style={{ top:'10%', right:'5%' }} color="#ff5d22" size={280} animDur={18} />
        <div className="relative z-20 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                AppGallop vs Legacy Platforms
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0">
                Built for the cloud era —{' '}
                <span className="text-[#ff5d22]">not retrofitted for it</span>
              </h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Legacy */}
            <Reveal delay={0.1}>
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-100">
                  <div className="w-2 h-2 rounded-full bg-slate-300" />
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Legacy / Traditional Platforms</span>
                </div>
                {T.comparison.map((c, i) => (
                  <div key={i} className={`flex items-center justify-between py-2.5 text-xs ${i < T.comparison.length - 1 ? 'border-b border-slate-50' : ''}`}>
                    <span className="font-bold text-slate-700">{c.f}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-red-50 flex items-center justify-center">
                        <Icon d={iconPaths.x} size={8} style={{ color:'#ef4444' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-slate-400 font-bold text-[10px] max-w-[140px] text-right leading-tight">{c.theirs}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
            {/* AppGallop */}
            <Reveal delay={0.16}>
              <div className="bg-gradient-to-br from-slate-950 to-slate-900 border border-[#ff5d22]/30 rounded-2xl p-6 relative overflow-hidden shadow-lg">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22] to-transparent" />
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/8">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#ff5d22] shadow-lg shadow-[#ff5d22]/50" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-white/60">AppGallop</span>
                  </div>
                  <span className="text-[8px] font-black uppercase tracking-widest text-[#ff5d22] flex items-center gap-1">
                    <Icon d={iconPaths.zap} size={8} style={{ color:'#ff5d22' }} /> Recommended
                  </span>
                </div>
                {T.comparison.map((c, i) => (
                  <div key={i} className={`flex items-center justify-between py-2.5 text-xs ${i < T.comparison.length - 1 ? 'border-b border-white/6' : ''}`}>
                    <span className="font-bold text-white/85">{c.f}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-[#ff5d22]/20 flex items-center justify-center">
                        <Icon d={iconPaths.check} size={8} style={{ color:'#ff5d22' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-slate-400 font-bold text-[10px] max-w-[140px] text-right leading-tight">{c.ours}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── WHY US (white, 2-col) ─── */}
      <section style={{ position:'relative', background:'#fff', padding:'80px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(0,0,0,0.025) 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <Orb style={{ bottom:'0%', left:'0%' }} color="#ff5d22" size={350} animDur={22} />
        <div style={{ position:'relative', zIndex:2, maxWidth:1000, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:48, alignItems:'center' }}>
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Purpose-Built for Telco
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight mb-4">
                Built for the Way Telcos Sell, Bundle, and Scale{' '}
                <span className="text-[#ff5d22]">Digital Services </span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p style={{ fontSize:15, color:'#6b7280', lineHeight:1.75 }}>
                Most platforms treat telcos as an afterthought. AppGallop was designed from the ground up for the unique realities of cloud distribution: multi-tier pricing, vendor complexity, reseller margin management, and customer lifecycle ownership.
              </p>
            </Reveal>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {T.whyFeatures.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="card-3d" style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, boxShadow:'0 1px 8px rgba(0,0,0,0.04)', cursor:'default' }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:'rgba(227,30,36,0.08)', border:'1px solid rgba(227,30,36,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Icon d={iconPaths.check} size={14} style={{ color:'#ff5d22' }} strokeWidth={2.5} />
                  </div>
                  <span style={{ fontSize:13, fontWeight:600, color:'#1f2937', lineHeight:1.5 }}>{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PILLARS (dark, 3-col) ─── */}
      <section style={{ position:'relative', background:'#08080c', padding:'80px 24px', overflow:'hidden' }}>
        <ParticleCanvas count={50} color="#3b82f6" />
        <Orb style={{ top:'5%', left:'15%' }} color="#ff5d22" size={400} animDur={20} />
        <GridFloor opacity={0.04} />
        <div style={{ position:'relative', zIndex:2, maxWidth:1100, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:48 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
                Where Telcos Gain Full Control
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white mb-3">
                One Platform. <span className="text-[#ff5d22]">Every Layer.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p style={{ fontSize:14, color:'rgba(255,255,255,0.5)', maxWidth:520, margin:'0 auto', lineHeight:1.7 }}>
                AppGallop's modular solution stack covers the full commercial lifecycle from partner onboarding to revenue recognition with deep integrations that eliminate glue code.
              </p>
            </Reveal>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20 }}>
            {T.pillars.map((p, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <TiltCard style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:16, padding:'32px 24px', textAlign:'center', backdropFilter:'blur(16px)', position:'relative', overflow:'hidden', height:'100%' }}>
                  <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:`linear-gradient(90deg,transparent,rgba(227,30,36,${0.3 + i * 0.15}),transparent)` }} />
                  <div style={{ position:'relative', display:'inline-block', marginBottom:20 }}>
                    <div style={{ position:'absolute', inset:-10, borderRadius:'50%', background:'rgba(227,30,36,0.12)', filter:'blur(12px)' }} />
                    <div style={{ position:'relative', width:56, height:56, borderRadius:14, background:'rgba(227,30,36,0.15)', border:'1px solid rgba(227,30,36,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <p.LucideIcon size={22} className="text-[#ff5d22]" />
                    </div>
                  </div>
                  <h3 style={{ fontSize:17, fontWeight:800, color:'#fff', marginBottom:10 }}>{p.title}</h3>
                  <p style={{ fontSize:13, color:'rgba(255,255,255,0.55)', lineHeight:1.7, margin:0 }}>{p.desc}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── GTM BANNER (red gradient) ─── */}
      <section style={{ position:'relative', background:'linear-gradient(135deg,#ff5d22 0%,#b91c1c 50%,#991b1b 100%)', padding:'60px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(255,255,255,0.08) 1px,transparent 1px)', backgroundSize:'24px 24px' }} />
        <div style={{ position:'relative', zIndex:2, maxWidth:800, margin:'0 auto', textAlign:'center' }}>
          <Reveal>
            <span style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 12px', borderRadius:100, background:'rgba(255,255,255,0.15)', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', fontSize:9, fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase' as const, marginBottom:16 }}>
              GTM Speed + Operational Simplicity
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4">
              Launch Faster. Operate Leaner. Grow Smarter.
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:'rgba(255,255,255,0.85)', lineHeight:1.75, maxWidth:580, margin:'0 auto' }}>
              Reduce the time from vendor agreement to live customer offer. Pre-built catalog templates and ready-made storefronts mean your team spends less time configuring and more time selling.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── FEATURES GRID (#fafafa) ─── */}
      <section style={{ position:'relative', background:'#fafafa', padding:'80px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(227,30,36,0.03) 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <div style={{ position:'relative', zIndex:2, maxWidth:1200, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:48 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Platform Features
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0">
                The Features That Make Telcos <span className="text-[#ff5d22]">Competitive</span>
              </h2>
            </Reveal>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
            {T.features.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="card-3d" style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:14, padding:'24px 20px', position:'relative', overflow:'hidden', height:'100%', boxShadow:'0 1px 12px rgba(0,0,0,0.03)' }}>
                  <div style={{ width:44, height:44, borderRadius:10, background:'rgba(227,30,36,0.07)', border:'1px solid rgba(227,30,36,0.15)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:14 }}>
                    <f.LucideIcon size={18} className="text-[#ff5d22]" />
                  </div>
                  <h3 style={{ fontSize:15, fontWeight:800, color:'#111827', marginBottom:8 }}>{f.title}</h3>
                  <p style={{ fontSize:13, color:'#6b7280', lineHeight:1.65, margin:0 }}>{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── AI CAPABILITIES (dark) ─── */}
      <section style={{ position:'relative', background:'#0a0a0e', padding:'80px 24px', overflow:'hidden' }}>
        <ParticleCanvas count={50} color="#8b5cf6" />
        <Orb style={{ top:'-5%', right:'-8%' }} color="#ff5d22" size={400} animDur={19} />
        <div style={{ position:'relative', zIndex:2, maxWidth:840, margin:'0 auto', textAlign:'center' }}>
          <Reveal>
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest">
                AI Capabilities
              </span>
              <span className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-white/40">
                <Brain size={11} className="text-[#ff5d22]" /> Powered by AppGallop AI
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4 leading-tight">
              AI that drives <span className="text-[#ff5d22]">monetization</span>, not just automation
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:'rgba(255,255,255,0.55)', lineHeight:1.75, maxWidth:580, margin:'0 auto 40px' }}>
              AppGallop's AI layer surfaces pricing signals, predicts churn before it happens, and recommends bundle configurations that maximize revenue per partner.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div style={{ display:'flex', justifyContent:'center', gap:10, flexWrap:'wrap' }}>
              {T.aiCapabilities.map((tag, i) => (
                <span key={i} style={{ padding:'8px 16px', borderRadius:100, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.7)', fontSize:12, fontWeight:600 }}>
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── CLARITY (white, centered) ─── */}
      <section style={{ position:'relative', background:'#fff', padding:'80px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(0,0,0,0.025) 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <Orb style={{ top:'0%', right:'0%' }} color="#ff5d22" size={300} animDur={17} />
        <div style={{ position:'relative', zIndex:2, maxWidth:800, margin:'0 auto', textAlign:'center' }}>
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
              Connectivity revenue is declining. Techcos choose growth.
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-4">
              One Platform. <span className="text-[#ff5d22]">Total Commercial Clarity.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:'#6b7280', lineHeight:1.75 }}>
              Stop managing partner lists across disconnected vendor portals. Centralize your entire cloud catalog, apply your own pricing layer, and present a clean, consistent marketplace to your customers and resellers.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── PROVEN RESULTS (#fafafa) ─── */}
      <section style={{ position:'relative', background:'#fafafa', padding:'80px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(227,30,36,0.03) 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <div style={{ position:'relative', zIndex:2, maxWidth:920, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Proven Results
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0">
                Why Leading Telcos Choose <span className="text-[#ff5d22]">AppGallop</span>
              </h2>
            </Reveal>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:20 }}>
            {T.results.map((m, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <TiltCard style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:14, padding:'32px 24px', textAlign:'center', boxShadow:'0 2px 16px rgba(0,0,0,0.04)', position:'relative', overflow:'hidden' }}>
                  <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg,#ff5d22,rgba(227,30,36,0.3))' }} />
                  <div style={{ fontSize:48, fontWeight:900, color:'#2563EB', lineHeight:1, marginBottom:8 }}>{m.val}</div>
                  <div style={{ fontSize:12, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase' as const, color:'#9ca3af', marginBottom:8 }}>{m.label}</div>
                  <div style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>{m.org}</div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25}>
            <div style={{ background:'linear-gradient(135deg,#111,#1c1c1c)', borderRadius:16, padding:'36px 32px', border:'1px solid rgba(255,255,255,0.08)', position:'relative', overflow:'hidden' }}>
              <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:'linear-gradient(90deg,transparent,rgba(227,30,36,0.6),transparent)' }} />
              <div style={{ display:'flex', gap:16, alignItems:'flex-start' }}>
                <div style={{ width:40, height:40, borderRadius:10, background:'rgba(227,30,36,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2 }}>
                  <Icon d={iconPaths.bar} size={18} style={{ color:'#ff5d22' }} />
                </div>
                <p style={{ fontSize:14, color:'rgba(255,255,255,0.75)', lineHeight:1.75, margin:0 }}>
                  A tier-1 APAC telco managing multiple hyperscaler relationships and over 800 enterprise customers consolidated their commercial operations onto AppGallop. Within 90 days, billing disputes dropped to near zero, partner activation time was cut by 50%, and the commercial team reclaimed 40 hours per week previously lost to manual reconciliation.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── ACTIVATION (dark) ─── */}
      <section style={{ position:'relative', background:'#08080c', padding:'80px 24px', overflow:'hidden' }}>
        <ParticleCanvas count={70} />
        <Orb style={{ top:'5%', left:'10%' }} color="#ff5d22" size={450} animDur={20} />
        <GridFloor opacity={0.06} />
        <div style={{ position:'relative', zIndex:2, maxWidth:800, margin:'0 auto', textAlign:'center' }}>
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
              Your Techco Transformation, Activated
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4 leading-tight">
              The Platform Behind <span className="text-[#ff5d22]">Leading Telco</span> Transformations
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:'rgba(255,255,255,0.55)', lineHeight:1.75, maxWidth:580, margin:'0 auto 32px' }}>
              Join a growing network of telcos who have replaced fragile BSS/OSS stacks with a single platform built for digital scale.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/contact">
              <button style={{ padding:'12px 32px', borderRadius:10, background:'#ff5d22', color:'#fff', fontWeight:700, fontSize:12, letterSpacing:'0.08em', textTransform:'uppercase' as const, border:'none', cursor:'pointer', animation:'pulse3d 3s ease infinite', display:'inline-flex', alignItems:'center', gap:8 }}>
                Book a Demo
                <Icon d={iconPaths.arrow} size={14} style={{ color:'#fff' }} strokeWidth={2} />
              </button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ─── PLATFORM METRICS (white) ─── */}
      <section style={{ position:'relative', background:'#fff', padding:'80px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(0,0,0,0.025) 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <Orb style={{ bottom:'0%', right:'0%' }} color="#ff5d22" size={300} animDur={21} />
        <div style={{ position:'relative', zIndex:2, maxWidth:920, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Platform Metrics
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-3">
                Designed for Operators Who Need <span className="text-[#ff5d22]">Results, Not Roadmaps</span>
              </h2>
            </Reveal>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:32 }}>
            {T.platformFeatures.map((item, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <TiltCard style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:14, padding:'24px 20px', textAlign:'center', boxShadow:'0 1px 12px rgba(0,0,0,0.03)', position:'relative', overflow:'hidden', height:'100%' }}>
                  <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg,#ff5d22,rgba(227,30,36,0.2))' }} />
                  <div style={{ fontSize:32, fontWeight:900, color:'#2563EB', lineHeight:1, marginBottom:6 }}>{item.val}</div>
                  <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' as const, color:'#9ca3af', marginBottom:8 }}>{item.label}</div>
                  <p style={{ fontSize:12, color:'#6b7280', lineHeight:1.6, margin:0 }}>{item.desc}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <div style={{ background:'linear-gradient(135deg,#111,#1c1c1c)', borderRadius:16, padding:'40px 36px', textAlign:'center', border:'1px solid rgba(255,255,255,0.06)', position:'relative', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
              <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:'linear-gradient(90deg,transparent,#ff5d22,transparent)' }} />
              <div style={{ width:48, height:48, borderRadius:12, background:'rgba(227,30,36,0.2)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                <Icon d={iconPaths.msg} size={20} style={{ color:'#ff5d22' }} />
              </div>
              <blockquote style={{ fontSize:18, fontStyle:'italic', color:'rgba(255,255,255,0.85)', fontWeight:400, lineHeight:1.65, maxWidth:520, margin:'0 auto 16px' }}>
                "Before AppGallop, launching a new cloud product in a new market took us three months. Now it takes three weeks."
              </blockquote>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase' as const, color:'rgba(255,255,255,0.35)' }}>
                VP of Commercial, Tier-1 APAC Telco
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section style={{ position:'relative', background:'#fafafa', padding:'80px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(227,30,36,0.03) 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <Orb style={{ top:'15%', left:'-8%' }} color="#ff5d22" size={280} animDur={19} />
        <div style={{ position:'relative', zIndex:2, maxWidth:700, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                FAQ
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 style={{ fontSize:'clamp(28px,4vw,48px)', fontWeight:800, marginBottom:0, color:'#1f2937' }}>
                Frequently Asked Questions
              </h2>
            </Reveal>
          </div>
          {T.faq.map((item, i) => (
            <FAQItem key={i} q={item.q} a={item.a} idx={i} />
          ))}
        </div>
      </section>

      {/* ─── FINAL CTA (very dark) ─── */}
      <section style={{ position:'relative', background:'#06060a', padding:'100px 24px', overflow:'hidden' }}>
        <ParticleCanvas count={90} />
        <GridFloor opacity={0.06} />
        <Orb style={{ top:'10%', left:'10%' }} color="#ff5d22" size={450} animDur={18} />
        <Orb style={{ bottom:'10%', right:'5%' }} color="#3b82f6" size={380} animDur={22} animDelay={4} />
        <div style={{ position:'relative', zIndex:2, maxWidth:780, margin:'0 auto', textAlign:'center' }}>
          <Reveal>
            <div style={{ fontSize:12, fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase' as const, color:'rgba(255,255,255,0.4)', marginBottom:16 }}>
              Ready to Transform?
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 style={{ fontSize:'clamp(32px,5vw,64px)', color:'#fff', fontWeight:800, marginBottom:16, lineHeight:1.05 }}>
              Your Techco Transformation <span style={{ color:'#ff5d22' }}>Starts Here</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:'rgba(255,255,255,0.55)', lineHeight:1.75, maxWidth:500, margin:'0 auto 32px' }}>
              See how AppGallop can be live in your environment within 6 weeks and see ROI within the first year.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
              <Link to="/contact">
                <button style={{ padding:'12px 32px', borderRadius:10, background:'#ff5d22', color:'#fff', fontWeight:700, fontSize:12, letterSpacing:'0.08em', textTransform:'uppercase' as const, border:'none', cursor:'pointer', animation:'pulse3d 3s ease infinite', display:'inline-flex', alignItems:'center', gap:8 }}>
                  Book a Demo
                  <Icon d={iconPaths.arrow} size={14} style={{ color:'#fff' }} strokeWidth={2} />
                </button>
              </Link>
              <Link to="/platform/integrations">
                <button style={{ padding:'12px 32px', borderRadius:10, background:'transparent', color:'rgba(255,255,255,0.75)', fontWeight:700, fontSize:12, letterSpacing:'0.08em', textTransform:'uppercase' as const, border:'1px solid rgba(255,255,255,0.18)', cursor:'pointer' }}>
                  Explore Integrations
                </button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <ContactSection id="contact" />
    </div>
  );
}
